import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ANIMATED_FLAG = 'homeAnimationsInit';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const hasSupportedHero = () => Boolean(document.querySelector('.home-hero, .about-hero'));

const toArray = (input) => {
  if (!input) {
    return [];
  }

  return Array.isArray(input) ? input.filter(Boolean) : [input];
};

const setWillChange = (elements, value = 'transform, opacity') => {
  toArray(elements).forEach((element) => {
    if (element && element.style) {
      element.style.willChange = value;
    }
  });
};

const clearWillChange = (elements) => {
  setWillChange(elements, 'auto');
};

const revealSection = (selector, options = {}) => {
  const sections = document.querySelectorAll(selector);

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    if (!section || section.dataset.homeAnimated === 'true') {
      return;
    }

    const fromVars = {
      y: 48,
      opacity: 0,
      ...options.from,
    };

    const toVars = {
      y: 0,
      opacity: 1,
      duration: 1,
      ease: 'power3.out',
      ...options.to,
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        once: true,
        markers: false,
        ...(options.scrollTrigger || {}),
      },
      onStart: () => setWillChange(section),
      onComplete: () => {
        clearWillChange(section);
        section.dataset.homeAnimated = 'true';
        if (typeof options.onComplete === 'function') {
          options.onComplete(section);
        }
      },
    };

    gsap.fromTo(section, fromVars, toVars);
  });
};

const revealChildren = (sectionSelector, childSelector, options = {}) => {
  const sections = document.querySelectorAll(sectionSelector);

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    if (!section || section.dataset.homeChildrenAnimated === 'true') {
      return;
    }

    const children = section.querySelectorAll(childSelector);

    if (!children.length) {
      return;
    }

    const animationTarget = options.targetResolver ? options.targetResolver(children) : children;
    const targets = toArray(animationTarget);

    if (!targets.length) {
      return;
    }

    gsap.fromTo(
      targets,
      {
        y: 40,
        opacity: 0,
        scale: 0.98,
        ...options.from,
      },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.95,
        ease: 'power3.out',
        stagger: 0.1,
        ...options.to,
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          once: true,
          markers: false,
          ...(options.scrollTrigger || {}),
        },
        onStart: () => setWillChange(targets),
        onComplete: () => {
          clearWillChange(targets);
          section.dataset.homeChildrenAnimated = 'true';
          if (typeof options.onComplete === 'function') {
            options.onComplete(section);
          }
        },
      }
    );
  });
};

const parseNumberToken = (value) => {
  const normalized = value.replace(/\s/g, '');
  const match = normalized.match(/-?\d+[\d.,]*/);

  if (!match) {
    return null;
  }

  const rawNumeric = match[0];
  const start = match.index || 0;
  const end = start + rawNumeric.length;
  const prefix = value.slice(0, start);
  const suffix = value.slice(end);

  const hasComma = rawNumeric.includes(',');
  const hasDot = rawNumeric.includes('.');

  let decimalSeparator = null;

  if (hasComma && hasDot) {
    decimalSeparator = rawNumeric.lastIndexOf(',') > rawNumeric.lastIndexOf('.') ? ',' : '.';
  } else if (hasComma) {
    decimalSeparator = ',';
  } else if (hasDot) {
    decimalSeparator = '.';
  }

  let sanitized = rawNumeric;
  if (decimalSeparator) {
    const thousandsRegex = decimalSeparator === ',' ? /\./g : /,/g;
    sanitized = sanitized.replace(thousandsRegex, '');
    sanitized = sanitized.replace(decimalSeparator, '.');
  }

  const numericValue = Number(sanitized);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  const decimalCount = decimalSeparator ? rawNumeric.split(decimalSeparator)[1]?.length || 0 : 0;

  return {
    value: numericValue,
    prefix,
    suffix,
    decimalCount,
    locale: 'pt-BR',
  };
};

const formatAnimatedNumber = (value, metadata) => {
  const formatter = new Intl.NumberFormat(metadata.locale, {
    minimumFractionDigits: metadata.decimalCount,
    maximumFractionDigits: metadata.decimalCount,
  });

  return `${metadata.prefix}${formatter.format(value)}${metadata.suffix}`;
};

const animateNumbers = (sectionSelector) => {
  const sections = document.querySelectorAll(sectionSelector);

  if (!sections.length) {
    return;
  }

  sections.forEach((section) => {
    const values = section.querySelectorAll('.big-numbers__value, .previous-events__metric-value');

    if (!values.length) {
      return;
    }

    ScrollTrigger.create({
      trigger: section,
      start: 'top 80%',
      once: true,
      markers: false,
      onEnter: () => {
        values.forEach((node) => {
          const originalText = node.textContent ? node.textContent.trim() : '';
          const parsed = parseNumberToken(originalText);

          if (!parsed) {
            return;
          }

          const state = { value: 0 };

          gsap.to(state, {
            value: parsed.value,
            duration: 1.1,
            ease: 'expo.out',
            onStart: () => setWillChange(node, 'contents'),
            onUpdate: () => {
              node.textContent = formatAnimatedNumber(state.value, parsed);
            },
            onComplete: () => {
              node.textContent = originalText;
              clearWillChange(node);
            },
          });
        });
      },
    });
  });
};

const initHeroAnimation = () => {
  const heroConfigs = [
    {
      rootSelector: '.home-hero',
      mediaSelector: '.home-hero__image',
      overlaySelector: '.home-hero__overlay',
      titleSelector: '.home-hero__title',
    },
    {
      rootSelector: '.about-hero',
      mediaSelector: '.about-hero__image',
      overlaySelector: '.about-hero__overlay',
      titleSelector: '.about-hero__title',
    },
  ];

  heroConfigs.forEach(({ rootSelector, mediaSelector, overlaySelector, titleSelector }) => {
    const hero = document.querySelector(rootSelector);
    if (!hero) {
      return;
    }

    const mediaImage = hero.querySelector(mediaSelector);
    const overlay = hero.querySelector(overlaySelector);
    const title = hero.querySelector(titleSelector);
    const header = document.querySelector('#site-header.header');

    ScrollTrigger.create({
      trigger: hero,
      start: 'top 80%',
      once: true,
      markers: false,
      onEnter: () => {
        const tl = gsap.timeline({ defaults: { duration: 1, ease: 'power3.out' } });

        if (mediaImage) {
          tl.fromTo(
            mediaImage,
            { scale: 1.08 },
            {
              scale: 1,
              duration: 1.15,
              onStart: () => setWillChange(mediaImage),
              onComplete: () => clearWillChange(mediaImage),
            }
          );
        }

        if (overlay) {
          tl.fromTo(
            overlay,
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.95,
            },
            '<0.15'
          );
        }

        if (title) {
          tl.fromTo(
            title,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 1,
              onStart: () => setWillChange(title),
              onComplete: () => clearWillChange(title),
            },
            '<0.05'
          );
        }

        if (header && !header.dataset.homeHeaderAnimated) {
          gsap.fromTo(
            header,
            { y: -16 },
            {
              y: 0,
              duration: 0.9,
              ease: 'power3.out',
              clearProps: 'transform',
            }
          );
          header.dataset.homeHeaderAnimated = 'true';
        }
      },
    });
  });
};

const initSectionReveals = () => {
  revealSection('.upcoming-events__header, .previous-events__header, .partner-testimonials__content, .strategic-mentorship__header', {
    from: { y: 36, opacity: 0 },
    to: { duration: 0.9 },
  });

  revealSection('.institutional-highlight', {
    from: { y: 42, opacity: 0 },
    to: { duration: 1.05 },
  });

  revealChildren('.institutional-highlight', '.institutional-highlight__media, .institutional-highlight__title', {
    from: { y: 36, opacity: 0 },
    to: { stagger: 0.14, duration: 1 },
  });
};

const initCardsAnimation = () => {
  revealChildren('.upcoming-events', '.upcoming-events__card .upcoming-events__image-wrap, .upcoming-events__card .upcoming-events__content', {
    from: { y: 40, opacity: 0, scale: 0.98 },
    to: { stagger: 0.1, duration: 0.95 },
  });

  revealChildren('.previous-events', '.previous-events__feedback-badge, .previous-events__center, .previous-events__metric', {
    from: { y: 40, opacity: 0, scale: 0.98 },
    to: { stagger: 0.1, duration: 1 },
  });

  revealChildren('.partner-testimonials', '.partner-testimonials__card', {
    from: { y: 40, opacity: 0, scale: 0.98 },
    to: { stagger: 0.12, duration: 0.95 },
  });

  revealChildren('.strategic-mentorship', '.strategic-mentorship__main, .strategic-mentorship__cta-card, .strategic-mentorship__side-media', {
    from: { y: 40, opacity: 0, scale: 0.98 },
    to: { stagger: 0.12, duration: 1 },
  });
};

const initBigNumbersAnimation = () => {
  revealChildren('.big-numbers', '.big-numbers__item', {
    from: { y: 40, opacity: 0, scale: 0.98 },
    to: { stagger: 0.1, duration: 0.95 },
  });

  animateNumbers('.big-numbers');
  animateNumbers('.previous-events');
};

const initFaqAnimation = () => {
  revealSection('.home-faq .home-faq__title', {
    from: { y: 32, opacity: 0 },
    to: { duration: 0.9 },
  });

  revealChildren('.home-faq', '.home-faq__tabs .home-faq__tab, .home-faq__panel.is-active .home-faq__item', {
    from: { y: 34, opacity: 0, scale: 0.99 },
    to: { stagger: 0.08, duration: 0.9 },
  });
};

const initFooterAnimation = () => {
  revealChildren('.site-footer', '.site-footer__brand, .site-footer__column, .site-footer__bottom', {
    from: { y: 34, opacity: 0 },
    to: { stagger: 0.1, duration: 0.9 },
  });
};

const showWithoutAnimation = () => {
  const selectors = [
    '.home-hero__image',
    '.home-hero__overlay',
    '.home-hero__title',
    '.about-hero__image',
    '.about-hero__overlay',
    '.about-hero__title',
    '.upcoming-events__header',
    '.upcoming-events__card',
    '.previous-events__header',
    '.previous-events__feedback-badge',
    '.previous-events__center',
    '.previous-events__metric',
    '.partner-testimonials__content',
    '.partner-testimonials__card',
    '.institutional-highlight',
    '.institutional-highlight__media',
    '.institutional-highlight__title',
    '.strategic-mentorship__header',
    '.strategic-mentorship__main',
    '.strategic-mentorship__cta-card',
    '.strategic-mentorship__side-media',
    '.big-numbers__item',
    '.home-faq__title',
    '.home-faq__tab',
    '.home-faq__item',
    '.site-footer__brand',
    '.site-footer__column',
    '.site-footer__bottom',
  ];

  gsap.set(selectors.join(', '), {
    clearProps: 'all',
    opacity: 1,
    y: 0,
    scale: 1,
  });
};

export const initHomeAnimations = () => {
  if (!document.body || !hasSupportedHero() || document.body.dataset[ANIMATED_FLAG] === 'true') {
    return;
  }

  document.body.dataset[ANIMATED_FLAG] = 'true';

  if (prefersReducedMotion()) {
    showWithoutAnimation();
    return;
  }

  gsap.defaults({
    duration: 1,
    ease: 'power3.out',
  });

  const media = gsap.matchMedia();

  media.add('(min-width: 1024px)', () => {
    initHeroAnimation();
    initSectionReveals();
    initCardsAnimation();
    initBigNumbersAnimation();
    initFaqAnimation();
    initFooterAnimation();
  });

  media.add('(max-width: 1023px)', () => {
    initHeroAnimation();
    initSectionReveals();
    initCardsAnimation();
    initBigNumbersAnimation();
    initFaqAnimation();
    initFooterAnimation();
  });

  window.setTimeout(() => {
    ScrollTrigger.refresh();
  }, 250);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHomeAnimations);
} else {
  initHomeAnimations();
}
